import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MessageSquare, Star, Eye, EyeOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminTestimonialsTab({ testimonials, onRefresh }) {
  const headers = { Authorization: `Bearer ${localStorage.getItem('session_token')}` };

  const handleToggleVisibility = async (validationId, currentHidden) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/testimonials/${validationId}/toggle-visibility`, {}, { headers });
      toast.success(currentHidden ? 'Depoimento visível' : 'Depoimento oculto');
      onRefresh();
    } catch { toast.error('Erro ao atualizar'); }
  };

  const handleDelete = async (validationId) => {
    if (!confirm('Excluir este depoimento permanentemente?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/testimonials/${validationId}`, { headers });
      toast.success('Depoimento excluído');
      onRefresh();
    } catch { toast.error('Erro ao excluir'); }
  };

  const visibleCount = testimonials.filter(t => !t.hidden).length;
  const hiddenCount = testimonials.filter(t => t.hidden).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageSquare className="h-6 w-6 text-slate-600" />
          <h2 className="text-xl font-bold text-slate-900">Depoimentos dos Clientes</h2>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-green-100 text-green-700 border-green-200">{visibleCount} visíveis</Badge>
          <Badge className="bg-slate-100 text-slate-600 border-slate-200">{hiddenCount} ocultos</Badge>
        </div>
      </div>

      {testimonials.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><p className="text-slate-500">Nenhum depoimento encontrado</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {testimonials.map(t => (
            <Card key={t.validation_id} className={`border ${t.hidden ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-slate-200'}`}
              data-testid={`admin-testimonial-${t.validation_id}`}>
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="shrink-0">
                    {t.user_picture ? (
                      <img src={t.user_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        <span className="text-slate-500 font-bold text-sm">{(t.user_name || 'U')[0]}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-semibold text-slate-900 text-sm">{t.user_name || 'Usuário'}</span>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map(s => (
                          <Star key={s} className={`h-3.5 w-3.5 ${s <= t.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                        ))}
                      </div>
                      {t.hidden && <Badge className="bg-red-100 text-red-600 border-red-200 text-xs">Oculto</Badge>}
                    </div>
                    <p className="text-slate-700 text-sm mb-2">"{t.testimonial}"</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {t.coupon && (
                        <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                          {t.coupon.title} - {t.coupon.discount_percentage}% OFF
                        </Badge>
                      )}
                      <span className="text-xs text-slate-400">
                        {new Date(t.validated_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleToggleVisibility(t.validation_id, t.hidden)}
                      className={`text-xs ${t.hidden ? 'border-green-300 text-green-600 hover:bg-green-50' : 'border-amber-300 text-amber-600 hover:bg-amber-50'}`}
                      data-testid={`toggle-testimonial-${t.validation_id}`}>
                      {t.hidden ? <><Eye className="h-3.5 w-3.5 mr-1" /> Mostrar</> : <><EyeOff className="h-3.5 w-3.5 mr-1" /> Ocultar</>}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(t.validation_id)}
                      className="text-xs border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`delete-testimonial-${t.validation_id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
